import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useMemo, useRef, useState } from 'react'
import { GripVertical, Loader2, MapPin, Pencil, Plus, Search, Trash2, X } from 'lucide-react'
import { toast } from 'sonner'
import { APIProvider } from '@vis.gl/react-google-maps'
import { useQueryClient } from '@tanstack/react-query'
import {
  DndContext,
  PointerSensor,
  KeyboardSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { restrictToVerticalAxis, restrictToParentElement } from '@dnd-kit/modifiers'
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import ReactMarkdown from 'react-markdown'
import { Button } from '~/client/components/ui/button'
import { Input } from '~/client/components/ui/input'
import { GoogleMapsAutocomplete } from '~/client/components/ui/google-maps-autocomplete'
import { useAuthStore } from '~/client/common/stores/auth'
import { useCreateGhostVenue } from '~/client/venue/use-create-ghost-venue.hook'
import { submitNotifyTagAdded } from '~/client/venue-tag/venue-tag.api'
import { TagDialog, type TagDialogValue } from '~/client/components/tag-settings/TagDialog'
import { GroupDialog, type GroupDialogValue } from '~/client/components/tag-settings/GroupDialog'
import { getTagColorById } from '~/shared/venue-tag/tag-colors'
import { hasStandardTagsAccess, hasVenueTagAdminAccess } from '~/shared/venue-tag/access'
import {
  useAddMembership,
  useCreateGroup,
  useCreateTag,
  useDeleteGroup,
  useDeleteTag,
  useRemoveMembership,
  useReorderGroups,
  useReorderTags,
  useTaggedVenues,
  useUpdateGroup,
  useUpdateTag,
  useVenueTagGroups,
} from '~/client/venue-tag/venue-tag.hooks'
import type { VenueTagDto, VenueTagGroupDto } from '~/shared/venue-tag/venue-tag.api.schema'
import { cn } from '~/shared/common/utils'

export const Route = createFileRoute('/_app/tag-settings')({
  component: TagSettingsPage,
})

type TagDialogState =
  | { instanceId: number; open: boolean; mode: 'create'; groupId: number }
  | { instanceId: number; open: boolean; mode: 'edit'; tagId: number }
  | null

type GroupDialogState =
  | { instanceId: number; open: boolean; mode: 'create' }
  | { instanceId: number; open: boolean; mode: 'edit'; groupId: number }
  | null

const sidebarDashedActionClassName =
  'inline-flex items-center gap-1 rounded-md border border-dashed border-[#e4e1e9] bg-white px-2 py-1 text-[11px] font-medium text-[#9b9b9a] transition-colors hover:border-[#8a3dff] hover:bg-[#8a3dff]/5 hover:text-[#8a3dff]'

const dialogCloseFallbackMs = 240

type OpenTagDialogInput =
  | { mode: 'create'; groupId: number }
  | { mode: 'edit'; tagId: number }

type OpenGroupDialogInput =
  | { mode: 'create' }
  | { mode: 'edit'; groupId: number }

function TagSettingsPage() {
  const navigate = useNavigate()
  const userDetail = useAuthStore((s) => s.userDetail)
  const orgName = userDetail?.active_entity?.name || 'Your team'
  const hasTagsFeature = hasStandardTagsAccess(userDetail?.active_entity?.features)
  const hasAccess = hasVenueTagAdminAccess(userDetail?.active_entity?.features, userDetail?.membership_type)

  const groupsQuery = useVenueTagGroups({ enabled: hasTagsFeature })
  const groups: VenueTagGroupDto[] = groupsQuery.data?.groups ?? []

  const [selectedTagId, setSelectedTagId] = useState<number | null>(null)

  const [optimisticTagOrder, setOptimisticTagOrder] = useState<Record<number, number[]>>({})
  const [optimisticGroupOrder, setOptimisticGroupOrder] = useState<number[] | null>(null)

  const [tagDialog, setTagDialog] = useState<TagDialogState>(null)
  const [groupDialog, setGroupDialog] = useState<GroupDialogState>(null)
  const dialogInstanceId = useRef(0)

  const createGroup = useCreateGroup()
  const updateGroup = useUpdateGroup()
  const deleteGroup = useDeleteGroup()
  const reorderGroups = useReorderGroups()
  const createTag = useCreateTag()
  const updateTag = useUpdateTag()
  const deleteTag = useDeleteTag()
  const reorderTags = useReorderTags()
  const addMembership = useAddMembership()
  const removeMembership = useRemoveMembership()

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  )

  function openTagDialog(next: OpenTagDialogInput) {
    dialogInstanceId.current += 1
    setTagDialog({ ...next, instanceId: dialogInstanceId.current, open: true })
  }

  function closeTagDialog() {
    setTagDialog((current) => (current ? { ...current, open: false } : current))
    window.setTimeout(clearClosedTagDialog, dialogCloseFallbackMs)
  }

  function clearClosedTagDialog() {
    setTagDialog((current) => (current && !current.open ? null : current))
  }

  function openGroupDialog(next: OpenGroupDialogInput) {
    dialogInstanceId.current += 1
    setGroupDialog({ ...next, instanceId: dialogInstanceId.current, open: true })
  }

  function closeGroupDialog() {
    setGroupDialog((current) => (current ? { ...current, open: false } : current))
    window.setTimeout(clearClosedGroupDialog, dialogCloseFallbackMs)
  }

  function clearClosedGroupDialog() {
    setGroupDialog((current) => (current && !current.open ? null : current))
  }

  const orderedGroups: VenueTagGroupDto[] = useMemo(() => {
    if (!optimisticGroupOrder) return groups
    const byId = new Map(groups.map((g) => [g.id, g] as const))
    const ordered: VenueTagGroupDto[] = []
    for (const id of optimisticGroupOrder) {
      const g = byId.get(id)
      if (g) {
        ordered.push(g)
        byId.delete(id)
      }
    }
    for (const g of byId.values()) ordered.push(g)
    return ordered
  }, [groups, optimisticGroupOrder])

  const getOrderedTags = (group: VenueTagGroupDto): VenueTagDto[] => {
    const override = optimisticTagOrder[group.id]
    if (!override) return group.tags
    const byId = new Map(group.tags.map((t) => [t.id, t] as const))
    const ordered: VenueTagDto[] = []
    for (const id of override) {
      const tag = byId.get(id)
      if (tag) {
        ordered.push(tag)
        byId.delete(id)
      }
    }
    for (const tag of byId.values()) ordered.push(tag)
    return ordered
  }

  const { selectedTag, selectedGroup } = useMemo(() => {
    let firstAvailable: { selectedTag: VenueTagDto; selectedGroup: VenueTagGroupDto } | null = null
    for (const g of orderedGroups) {
      const first = g.tags[0]
      if (first && !firstAvailable) {
        firstAvailable = { selectedTag: first, selectedGroup: g }
      }
      const t = g.tags.find((x) => x.id === selectedTagId)
      if (t) return { selectedTag: t, selectedGroup: g }
    }
    return firstAvailable ?? { selectedTag: null, selectedGroup: null }
  }, [orderedGroups, selectedTagId])

  const editingTag = useMemo(() => {
    if (tagDialog?.mode !== 'edit') return undefined
    for (const g of groups) {
      const t = g.tags.find((x) => x.id === tagDialog.tagId)
      if (t) return t
    }
    return undefined
  }, [tagDialog, groups])

  const editingGroup = useMemo(() => {
    if (groupDialog?.mode !== 'edit') return undefined
    return groups.find((g) => g.id === groupDialog.groupId)
  }, [groupDialog, groups])

  function handleTagDialogSubmit(value: TagDialogValue) {
    if (tagDialog?.mode === 'create') {
      createTag.mutate(
        {
          groupId: tagDialog.groupId,
          label: value.name,
          description: value.description,
          colorId: value.colorId,
        },
        {
          onSuccess: (res) => {
            toast.success(`Tag "${res.tag.label}" created`)
            closeTagDialog()
            setSelectedTagId(res.tag.id)
          },
          onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to create tag'),
        },
      )
      return
    }
    if (tagDialog?.mode === 'edit') {
      updateTag.mutate(
        {
          tagId: tagDialog.tagId,
          label: value.name,
          description: value.description,
          colorId: value.colorId,
        },
        {
          onSuccess: (res) => {
            toast.success(`Tag "${res.tag.label}" updated`)
            closeTagDialog()
          },
          onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to update tag'),
        },
      )
    }
  }

  function handleGroupDialogSubmit(value: GroupDialogValue) {
    if (groupDialog?.mode === 'create') {
      createGroup.mutate(
        { name: value.name, description: value.description },
        {
          onSuccess: (res) => {
            toast.success(`Group "${res.group.name}" created`)
            closeGroupDialog()
          },
          onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to create group'),
        },
      )
      return
    }
    if (groupDialog?.mode === 'edit') {
      updateGroup.mutate(
        { groupId: groupDialog.groupId, name: value.name, description: value.description },
        {
          onSuccess: (res) => {
            toast.success(`Group "${res.group.name}" updated`)
            closeGroupDialog()
          },
          onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to update group'),
        },
      )
    }
  }

  function handleDeleteGroup() {
    if (groupDialog?.mode !== 'edit') return
    const group = groups.find((g) => g.id === groupDialog.groupId)
    if (!group) return
    const tagCount = group.tags.length
    const venueTagCount = group.tags.reduce((total, tag) => total + tag.venueCount, 0)
    deleteGroup.mutate(
      { groupId: group.id },
      {
        onSuccess: () => {
          toast.success(
            `Deleted "${group.name}" with ${tagCount} tag${tagCount === 1 ? '' : 's'} and ${venueTagCount} venue tag${venueTagCount === 1 ? '' : 's'}`,
          )
          closeGroupDialog()
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete group'),
      },
    )
  }

  function handleDeleteTag() {
    if (tagDialog?.mode !== 'edit') return
    if (!editingTag) return
    const tag = editingTag
    const venueTagCount = tag.venueCount
    deleteTag.mutate(
      { tagId: tag.id },
      {
        onSuccess: () => {
          toast.success(
            `Deleted "${tag.label}" and ${venueTagCount} venue tag${venueTagCount === 1 ? '' : 's'}`,
          )
          closeTagDialog()
        },
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to delete tag'),
      },
    )
  }

  function handleTagReorder(group: VenueTagGroupDto, event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const ids = getOrderedTags(group).map((t) => t.id)
    const oldIndex = ids.indexOf(Number(active.id))
    const newIndex = ids.indexOf(Number(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    const nextIds = arrayMove(ids, oldIndex, newIndex)
    setOptimisticTagOrder((prev) => ({ ...prev, [group.id]: nextIds }))
    reorderTags.mutate(
      { groupId: group.id, tagIds: nextIds },
      { onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to save tag order') },
    )
  }

  function handleGroupReorder(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const ids = orderedGroups.map((g) => g.id)
    const oldIndex = ids.indexOf(Number(active.id))
    const newIndex = ids.indexOf(Number(over.id))
    if (oldIndex < 0 || newIndex < 0) return
    const nextIds = arrayMove(ids, oldIndex, newIndex)
    setOptimisticGroupOrder(nextIds)
    reorderGroups.mutate(
      { groupIds: nextIds },
      { onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to save group order') },
    )
  }

  const isTagDialogSubmitting = tagDialog?.mode === 'create' ? createTag.isPending : updateTag.isPending
  const isGroupDialogSubmitting = groupDialog?.mode === 'create' ? createGroup.isPending : updateGroup.isPending
  if (!hasAccess) {
    return (
      <div className="min-h-full bg-[#FBF9F5]">
        <div className="mx-auto w-full max-w-[640px] px-8 py-20 text-center">
          <h1 className="text-[22px] font-bold tracking-tight text-[#18003D]">Tag Settings not enabled</h1>
          <p className="mt-2 text-sm text-[#55534e]">
            {hasTagsFeature
              ? `Only firm owners and administrators can manage tags for ${orgName}.`
              : `Tags aren't available for ${orgName} yet. Contact your Revelstreet rep to enable this feature.`}
          </p>
          <button
            type="button"
            onClick={() => {
              void navigate({ to: '/' })
            }}
            className="mt-4 inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold text-[#8a3dff] hover:bg-[#F3EDFC]"
          >
            Back home
          </button>
        </div>
      </div>
    )
  }

  return (
    <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY} libraries={['places']}>
    <div className="flex min-h-full flex-col bg-[#FBF9F5]">
      <div className="mx-auto w-full max-w-[1280px] px-8 pt-[18px]">
        <div className="flex items-start justify-between gap-6">
          <div>
            <h1 className="text-[26px] font-bold tracking-tight text-[#18003D]">{orgName} Tags Settings</h1>
            <p className="mt-1.5 max-w-[640px] text-sm text-[#55534e]">
              Create and manage tags that your team can use to organize venues. Every planner at {orgName} will see these tags when searching and browsing.
            </p>
          </div>
        </div>
      </div>

      <div className="mx-auto flex w-full max-w-[1280px] flex-1 items-start gap-6 px-8 pb-12 pt-6">
        <aside className="sticky top-20 w-[300px] flex-shrink-0 self-start rounded-xl border border-[#e4e1e9] bg-white p-2.5">
          <div className="flex items-center justify-between px-2 pb-2.5 pt-1.5">
            <span className="text-[16px] font-bold tracking-tight text-[#18003D]">Tag Groups</span>
            <button
              type="button"
              title="Add tag group"
              onClick={() => openGroupDialog({ mode: 'create' })}
              className={sidebarDashedActionClassName}
            >
              <Plus className="size-3" />
              Tag Group
            </button>
          </div>

          {groupsQuery.isLoading ? (
            <div className="px-2 py-3 text-[12px] text-[#9b9b9a]">Loading…</div>
          ) : orderedGroups.length === 0 ? (
            <div className="px-2 py-3 text-[12px] text-[#9b9b9a]">
              No tag groups yet. Click "Tag Group" to create one.
            </div>
          ) : (
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              modifiers={[restrictToVerticalAxis, restrictToParentElement]}
              onDragEnd={handleGroupReorder}
            >
              <SortableContext items={orderedGroups.map((g) => g.id)} strategy={verticalListSortingStrategy}>
                {orderedGroups.map((group, idx) => (
                  <SortableGroupSection
                    key={group.id}
                    group={group}
                    isLast={idx === orderedGroups.length - 1}
                    sensors={sensors}
                    orderedTags={getOrderedTags(group)}
                    selectedTagId={selectedTag?.id ?? null}
                    onEditGroup={() => openGroupDialog({ mode: 'edit', groupId: group.id })}
                    onAddTag={() => openTagDialog({ mode: 'create', groupId: group.id })}
                    onTagReorder={(e) => handleTagReorder(group, e)}
                    onSelectTag={(tag) => setSelectedTagId(tag.id)}
                  />
                ))}
              </SortableContext>
            </DndContext>
          )}
        </aside>

        <main className="min-h-0 min-w-0 flex-1">
          {selectedTag && selectedGroup ? (
            <TagDetailPanel
              key={selectedTag.id}
              tag={selectedTag}
              group={selectedGroup}
              orgName={orgName}
              onEdit={() => openTagDialog({ mode: 'edit', tagId: selectedTag.id })}
              addMembership={addMembership}
              removeMembership={removeMembership}
            />
          ) : groupsQuery.isLoading ? (
            <div className="rounded-xl border border-[#e4e1e9] bg-white p-12 text-center text-sm text-[#9b9b9a]">
              Loading tag groups…
            </div>
          ) : groups.length === 0 ? (
            <div className="rounded-xl border border-[#e4e1e9] bg-white p-12 text-center text-sm text-[#9b9b9a]">
              No tag groups yet. Create one to get started.
            </div>
          ) : (
            <div className="rounded-xl border border-[#e4e1e9] bg-white p-12 text-center text-sm text-[#9b9b9a]">
              Create a tag in any group to start tagging venues.
            </div>
          )}
        </main>
      </div>

      {tagDialog && (
        <TagDialog
          key={tagDialog.instanceId}
          open={tagDialog.open}
          onOpenChange={(open) => {
            if (!open && !isTagDialogSubmitting && !deleteTag.isPending) closeTagDialog()
          }}
          onCloseAnimationEnd={clearClosedTagDialog}
          mode={tagDialog.mode}
          groupName={
            tagDialog.mode === 'create'
              ? groups.find((g) => g.id === tagDialog.groupId)?.name ?? ''
              : (editingTag && groups.find((g) => g.id === editingTag.groupId)?.name) ?? ''
          }
          initial={
            editingTag
              ? { name: editingTag.label, description: editingTag.description ?? '', colorId: editingTag.colorId }
              : undefined
          }
          deleteLabel={editingTag?.label}
          deleteMembershipCount={editingTag?.venueCount ?? 0}
          onSubmit={handleTagDialogSubmit}
          onDelete={tagDialog.mode === 'edit' ? handleDeleteTag : undefined}
          isSubmitting={isTagDialogSubmitting}
          isDeleting={deleteTag.isPending}
        />
      )}

      {groupDialog && (
        <GroupDialog
          key={groupDialog.instanceId}
          open={groupDialog.open}
          onOpenChange={(open) => {
            if (!open && !isGroupDialogSubmitting && !deleteGroup.isPending) closeGroupDialog()
          }}
          onCloseAnimationEnd={clearClosedGroupDialog}
          mode={groupDialog.mode}
          initial={
            editingGroup
              ? { name: editingGroup.name, description: editingGroup.description ?? '' }
              : undefined
          }
          deleteGroupName={editingGroup?.name}
          deleteTagCount={editingGroup?.tags.length ?? 0}
          deleteMembershipCount={editingGroup?.tags.reduce((total, tag) => total + tag.venueCount, 0) ?? 0}
          onSubmit={handleGroupDialogSubmit}
          onDelete={groupDialog.mode === 'edit' ? handleDeleteGroup : undefined}
          isSubmitting={isGroupDialogSubmitting}
          isDeleting={deleteGroup.isPending}
        />
      )}
    </div>
    </APIProvider>
  )
}

function SortableGroupSection({
  group,
  isLast,
  sensors,
  orderedTags,
  selectedTagId,
  onEditGroup,
  onAddTag,
  onTagReorder,
  onSelectTag,
}: {
  group: VenueTagGroupDto
  isLast: boolean
  sensors: ReturnType<typeof useSensors>
  orderedTags: VenueTagDto[]
  selectedTagId: number | null
  onEditGroup: () => void
  onAddTag: () => void
  onTagReorder: (e: DragEndEvent) => void
  onSelectTag: (tag: VenueTagDto) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: group.id })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn('mb-2 pb-2.5', !isLast && 'border-b border-[#e4e1e9]')}
    >
      <div className="flex items-center justify-between rounded-md px-2 py-1">
        <button
          type="button"
          {...attributes}
          {...listeners}
          className="mr-1 flex size-4 shrink-0 cursor-grab items-center justify-center text-[#c5c1ca] active:cursor-grabbing"
          aria-label={`Reorder group ${group.name}`}
        >
          <GripVertical className="size-3" />
        </button>
        <span className="flex-1 truncate text-[14px] font-semibold tracking-tight text-[#18003D]">
          {group.name}
        </span>
        <button
          type="button"
          onClick={onEditGroup}
          title="Edit group settings"
          className="flex size-[22px] items-center justify-center rounded-md text-[#9b9b9a] hover:bg-[#FBF9F5]"
        >
          <Pencil className="size-3" />
        </button>
      </div>

      <div className="mt-1 flex flex-col gap-px pl-3">
        {orderedTags.length === 0 ? (
          <div className="px-2 py-1 text-[11px] text-[#9b9b9a]">No tags yet</div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            modifiers={[restrictToVerticalAxis, restrictToParentElement]}
            onDragEnd={onTagReorder}
          >
            <SortableContext items={orderedTags.map((t) => t.id)} strategy={verticalListSortingStrategy}>
              {orderedTags.map((tag) => (
                <SortableSidebarTagRow
                  key={tag.id}
                  tag={tag}
                  isSelected={tag.id === selectedTagId}
                  onClick={() => onSelectTag(tag)}
                />
              ))}
            </SortableContext>
          </DndContext>
        )}
      </div>

      <button
        type="button"
        onClick={onAddTag}
        className={cn(sidebarDashedActionClassName, 'ml-5 mt-1.5')}
      >
        <Plus className="size-3" />
        Tag
      </button>
    </div>
  )
}

function SortableSidebarTagRow({
  tag,
  isSelected,
  onClick,
}: {
  tag: VenueTagDto
  isSelected: boolean
  onClick: () => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: tag.id })
  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 20 : undefined,
  }
  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        'group flex w-full items-center gap-1 rounded-md py-1 pl-0.5 pr-1 ring-1 ring-transparent transition-colors',
        isSelected ? 'bg-[#F3EDFC] ring-[#d8b4fe]' : 'hover:bg-[#FBF9F5] hover:ring-[#e4e1e9]',
      )}
    >
      <button
        type="button"
        {...attributes}
        {...listeners}
        className="flex size-3 shrink-0 cursor-grab items-center justify-center text-[#c5c1ca] opacity-0 transition-opacity group-hover:opacity-100 active:cursor-grabbing"
        aria-label={`Reorder tag ${tag.label}`}
      >
        <GripVertical className="size-3" />
      </button>
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex-1 truncate px-1 text-left text-[13px]',
          isSelected ? 'font-semibold text-[#18003D]' : 'text-[#18003D]',
        )}
      >
        {tag.label}
      </button>
      <span className="min-w-[18px] shrink-0 text-right text-[11px] tabular-nums text-[#9b9b9a]">
        {tag.venueCount}
      </span>
    </div>
  )
}

type PendingAdd = {
  pendingId: string
  placeId: string
  name: string
  address: string
  // Set once createGhostVenue resolves. Used at render time to dedupe a
  // pending row against the real venue once the refetch lands it in the
  // tagged-venues list — without this, the row briefly appears twice.
  venueId: number | null
}

function TagDetailPanel({
  tag,
  group,
  orgName,
  onEdit,
  addMembership,
  removeMembership,
}: {
  tag: VenueTagDto
  group: VenueTagGroupDto
  orgName: string
  onEdit: () => void
  addMembership: ReturnType<typeof useAddMembership>
  removeMembership: ReturnType<typeof useRemoveMembership>
}) {
  const venuesQuery = useTaggedVenues({ tagId: tag.id, limit: 200 })
  const venues = venuesQuery.data?.venues ?? []
  const createGhostVenue = useCreateGhostVenue()
  const qc = useQueryClient()

  const [searchQuery, setSearchQuery] = useState('')
  const [adderOpen, setAdderOpen] = useState(false)
  // Reset on each open so the Google autocomplete starts empty.
  const [adderInputValue, setAdderInputValue] = useState('')
  const [pendingAdds, setPendingAdds] = useState<PendingAdd[]>([])

  const pendingRemoveVenueId = removeMembership.isPending ? (removeMembership.variables?.venueId ?? null) : null

  const filteredVenues = useMemo(() => {
    const q = searchQuery.trim().toLowerCase()
    if (!q) return venues
    return venues.filter((v) => v.name.toLowerCase().includes(q) || v.address.toLowerCase().includes(q))
  }, [venues, searchQuery])

  // Hide a pending row once its resolved venueId shows up in the real list —
  // otherwise the row appears twice for the moment between the cache refetch
  // landing and us removing the pending entry from local state. Pending rows
  // also respect the tagged-venues search input so the table stays coherent.
  const visiblePendingAdds = useMemo(() => {
    const realIds = new Set(venues.map((v) => v.id))
    const q = searchQuery.trim().toLowerCase()
    return pendingAdds.filter((p) => {
      if (p.venueId !== null && realIds.has(p.venueId)) return false
      if (!q) return true
      return p.name.toLowerCase().includes(q) || p.address.toLowerCase().includes(q)
    })
  }, [pendingAdds, venues, searchQuery])

  const tagColor = getTagColorById(tag.colorId)

  async function handleAddVenueFromPlace(placeDetails: {
    place_id?: string
    name?: string
    formatted_address?: string
  }) {
    const placeId = placeDetails.place_id
    const name = placeDetails.name?.trim() || 'Unnamed venue'
    const address = placeDetails.formatted_address?.trim() || ''
    if (!placeId) {
      toast.error('Could not read Google Place id. Try selecting again.')
      return
    }

    const pendingId = `pending-${placeId}-${Date.now()}`
    setPendingAdds((prev) => [...prev, { pendingId, placeId, name, address, venueId: null }])
    // Clear the input so the planner can search for the next venue without
    // waiting on the ghost-venue + membership pipeline below.
    setAdderInputValue('')

    try {
      const ghost = await createGhostVenue.mutateAsync({
        google_place_id: placeId,
        create_source: 'customer_ghost',
        // Tags are entity-scoped, not venue-group-scoped — allow tagging a
        // venue that exists in our DB under a different venue_group than the
        // user's active one. Otherwise the server throws "Venue exists in
        // different venue group" and the tag-add toast fails.
        allow_cross_venue_group: true,
      })
      if (!ghost.venue_id) throw new Error('Ghost venue creation returned no venue_id')

      // Stamp the resolved venueId on the pending row so the render-time
      // dedupe can hide it the instant the refetch lands the real row.
      const venueId = ghost.venue_id
      setPendingAdds((prev) =>
        prev.map((p) => (p.pendingId === pendingId ? { ...p, venueId } : p)),
      )

      await addMembership.mutateAsync({ tagId: tag.id, venueId })
      // useAddMembership.onSuccess already invalidates ['venue-tag']; await
      // here just to make sure the pending row is dropped after the refetch
      // settles (avoids a brief empty gap if the dedupe race lost). Dedupe
      // by venueId at render time still covers the overlap window.
      await qc.invalidateQueries({ queryKey: ['venue-tag', 'taggedVenues'] })
      setPendingAdds((prev) => prev.filter((p) => p.pendingId !== pendingId))
      toast.success(`Added ${name} to ${tag.label}`)
      // Fire-and-forget Slack notification. Server fans it out:
      //   - Always: #new-entity-specific-tag (any successful tag).
      //   - When the venue is an *existing* ghost: also #new-ghost-venue.
      // Brand-new ghost venues already trigger the createGhostVenue webhook
      // to #new-ghost-venue on insert, so we flag only the existing-ghost case.
      const venueWasExistingGhost =
        ghost.venue_create_source === 'customer_ghost' && ghost.venue_created !== true
      void submitNotifyTagAdded({
        tagId: tag.id,
        tagName: tag.label,
        venueId,
        venueName: name,
        venueWasExistingGhost,
      }).catch(() => {
        // Notify is fire-and-forget — server already logs failures.
      })
    } catch (err) {
      setPendingAdds((prev) => prev.filter((p) => p.pendingId !== pendingId))
      toast.error(
        err instanceof Error
          ? `Unable to tag ${name}: ${err.message}`
          : `Unable to tag ${name} to ${tag.label}. Try again or contact support@revelstreet.co.`,
      )
    }
  }

  function handleToggleAdder() {
    const nextOpen = !adderOpen
    setAdderOpen(nextOpen)
    if (!nextOpen) setAdderInputValue('')
  }

  function handleRemoveVenue(venue: { id: number; name: string }) {
    removeMembership.mutate(
      { tagId: tag.id, venueId: venue.id },
      {
        onSuccess: () => toast.success(`Removed "${venue.name}"`),
        onError: (err) => toast.error(err instanceof Error ? err.message : 'Failed to remove venue'),
      },
    )
  }

  return (
    <div className="rounded-xl border border-[#e4e1e9] bg-white">
      <div className="flex items-start justify-between gap-3 px-7 pt-7 pb-2">
        <div className="flex flex-wrap items-center gap-3">
          <h2 className="text-[24px] font-bold tracking-tight text-[#18003D]">{tag.label}</h2>
          <span
            className="inline-flex items-center rounded-full px-2.5 py-[3px] text-[12px] font-semibold"
            style={{ background: tagColor.bg, color: tagColor.text }}
          >
            {tag.label}
          </span>
          <span className="text-[12px] text-[#9b9b9a]">in {group.name}</span>
        </div>
        <button
          type="button"
          onClick={onEdit}
          title="Edit tag"
          className="flex size-8 shrink-0 items-center justify-center rounded-md border border-[#e4e1e9] text-[#55534e] hover:bg-[#FBF9F5]"
        >
          <Pencil className="size-3.5" />
        </button>
      </div>
      {tag.description && (
        <div className="px-7 pb-4 max-w-[640px] text-[13px] leading-relaxed text-[#55534e] [&_*:first-child]:mt-0 [&_*:last-child]:mb-0 [&_a]:text-[#8a3dff] [&_a]:underline [&_em]:italic [&_li]:my-0 [&_ol]:my-1 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:my-1 [&_strong]:font-semibold [&_strong]:text-[#18003D] [&_ul]:my-1 [&_ul]:list-disc [&_ul]:pl-5">
          <ReactMarkdown>{tag.description}</ReactMarkdown>
        </div>
      )}
      <div className="mx-7 border-b border-[#e4e1e9]" />

      <div className="px-7 pt-5 pb-3">
        <div className="mb-3 flex items-center justify-between">
          <div>
            <div className="text-[14px] font-bold text-[#18003D]">Tagged venues</div>
            {/* Optimistic count from the groups payload — already loaded by the time
                this panel renders, so the number doesn't flash 0 → N while
                useTaggedVenues fetches the full list. */}
            <div className="text-[12px] text-[#9b9b9a]">
              <strong className="font-semibold text-[#18003D]">{tag.venueCount}</strong> {tag.venueCount === 1 ? 'venue' : 'venues'} tagged
            </div>
          </div>
          <Button variant="revel" size="revel40" onClick={handleToggleAdder}>
            {adderOpen ? <X className="size-3.5" /> : <Plus className="size-3.5" />}
            {adderOpen ? 'Close' : 'Add venue'}
          </Button>
        </div>

        {adderOpen && (
          <div className="mb-3 rounded-[10px] border border-[#8a3dff]/40 bg-white px-4 py-3 shadow-sm">
            <div className="mb-2 text-[13px] font-semibold text-[#18003D]">
              Add a venue to <span style={{ color: tagColor.text }}>{tag.label}</span>
            </div>
            {/* Google Places autocomplete renders its results in an overlay
                Popover (not inline), so adding tags doesn't shift the rest of
                the panel around as suggestions come in. Mirroring the SRP
                Venue Name filter, we restrict to `establishment` +
                `point_of_interest` — that drops cities/neighborhoods/ZIPs
                without losing venues that Google classifies as POIs (e.g.
                landmarks, event halls, museums). */}
            <GoogleMapsAutocomplete
              value={adderInputValue}
              onChange={(v) => setAdderInputValue(v)}
              onPlaceSelect={(details) => void handleAddVenueFromPlace(details)}
              placeholder="Search for a venue to tag…"
              className="h-10"
              fields={['formatted_address', 'name', 'place_id']}
              types={['establishment', 'point_of_interest']}
            />
          </div>
        )}

        <div className="mb-3">
          <Input
            placeholder="Search tagged venues…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            icon={<Search className="size-3.5 text-[#9b9b9a]" />}
            className="h-10"
          />
        </div>

        <div className="overflow-hidden rounded-[10px] border border-[#e4e1e9]">
          <div className="grid grid-cols-[1fr_1.4fr_36px] gap-3 border-b border-[#e4e1e9] bg-[#FBF9F5] px-4 py-2.5 text-[11px] font-semibold uppercase tracking-wider text-[#9b9b9a]">
            <span>Venue</span>
            <span>Address</span>
            <span />
          </div>
          {venuesQuery.isLoading ? (
            // Render skeleton rows sized to the optimistic count (capped) so the
            // panel doesn't jump from a single "Loading…" line to a 200-row list.
            Array.from({ length: Math.max(1, Math.min(tag.venueCount || 1, 6)) }).map((_, idx, arr) => (
              <div
                key={idx}
                className={cn(
                  'grid grid-cols-[1fr_1.4fr_36px] items-center gap-3 px-4 py-3',
                  idx < arr.length - 1 && 'border-b border-[#e4e1e9]',
                )}
              >
                <div className="h-3 w-32 animate-pulse rounded bg-[#EEECE6]" />
                <div className="h-3 w-56 animate-pulse rounded bg-[#EEECE6]" />
                <span />
              </div>
            ))
          ) : filteredVenues.length === 0 && visiblePendingAdds.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-[#9b9b9a]">
              {searchQuery ? 'No venues match your search.' : 'No venues tagged yet. Click "Add venue" to add one.'}
            </div>
          ) : (
            <>
              {filteredVenues.map((venue, idx) => {
                const isRemoving = pendingRemoveVenueId === venue.id
                const isLastReal = idx === filteredVenues.length - 1
                return (
                  <div
                    key={venue.id}
                    className={cn(
                      'grid grid-cols-[1fr_1.4fr_36px] items-center gap-3 px-4 py-3 transition-opacity',
                      (!isLastReal || visiblePendingAdds.length > 0) && 'border-b border-[#e4e1e9]',
                      isRemoving && 'opacity-50',
                    )}
                    aria-busy={isRemoving || undefined}
                  >
                    <div className="text-[13px] font-semibold text-[#18003D]">{venue.name}</div>
                    <div className="inline-flex items-center gap-1.5 text-xs text-[#55534e]">
                      <MapPin className="size-3 text-[#9b9b9a]" />
                      {venue.address}
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveVenue(venue)}
                      disabled={isRemoving}
                      title={`Remove ${venue.name} from ${tag.label}`}
                      className="flex size-7 items-center justify-center rounded-md text-[#c5c1ca] transition-colors hover:bg-[#FDF8F8] hover:text-[#d04b46] disabled:cursor-wait"
                    >
                      {isRemoving ? <Loader2 className="size-3.5 animate-spin" /> : <Trash2 className="size-3.5" />}
                    </button>
                  </div>
                )
              })}
              {/* Pending rows render at the bottom — listTaggedVenues returns
                  rows by membership insertion order, so the newest venue
                  always lands at the end. Keeping pending rows in the same
                  position means no visual jump when the real row replaces
                  them (and dedupe-by-venueId hides the pending entry the
                  instant the refetch lands its real twin). */}
              {visiblePendingAdds.map((p, idx, arr) => (
                <div
                  key={p.pendingId}
                  className={cn(
                    'grid grid-cols-[1fr_1.4fr_36px] items-center gap-3 px-4 py-3',
                    idx < arr.length - 1 && 'border-b border-[#e4e1e9]',
                  )}
                  aria-busy="true"
                >
                  <div className="text-[13px] font-semibold text-[#18003D]">{p.name}</div>
                  <div className="inline-flex items-center gap-1.5 text-xs text-[#55534e]">
                    <MapPin className="size-3 text-[#9b9b9a]" />
                    {p.address}
                  </div>
                  <button
                    type="button"
                    disabled
                    aria-hidden="true"
                    tabIndex={-1}
                    className="flex size-7 items-center justify-center rounded-md text-[#c5c1ca]"
                  >
                    <Trash2 className="size-3.5" />
                  </button>
                </div>
              ))}
            </>
          )}
        </div>

        <div className="mt-3 mb-5 text-xs text-[#9b9b9a]">
          {venuesQuery.isLoading ? (
            <span>Loading tagged venues…</span>
          ) : (
            <>
              Showing {filteredVenues.length + visiblePendingAdds.length} of {venues.length + visiblePendingAdds.length} tagged venue{venues.length + visiblePendingAdds.length === 1 ? '' : 's'}
              {orgName ? <span className="ml-1">— visible to every planner at {orgName}</span> : null}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
